import { addDays, format, getDay, startOfDay } from "date-fns";

export interface TimeSlot {
  time: string;
  available: boolean;
}

export type SlotTakenCheck = (dateKey: string, time: string) => boolean;

const WEEKDAY_RANGES: Array<[number, number]> = [
  [8 * 60, 12 * 60 + 30],
  [14 * 60, 20 * 60],
];
const SATURDAY_RANGES: Array<[number, number]> = [[8 * 60, 12 * 60 + 30]];
const SLOT_STEP = 30;
const MIN_NOTICE_MS = 60 * 60 * 1000;

export function toDateKey(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

export function parseDateKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function toDateTime(dateKey: string, time: string): Date {
  const date = parseDateKey(dateKey);
  const [h, min] = time.split(":").map(Number);
  date.setHours(h, min, 0, 0);
  return date;
}

export function getBookableDays(count = 14, from = new Date()): Date[] {
  const days: Date[] = [];
  let cursor = startOfDay(from);
  while (days.length < count) {
    if (getDay(cursor) !== 0) days.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return days;
}

export function getSlotsForDay(
  date: Date,
  duration: number,
  isTaken: SlotTakenCheck,
  now = new Date()
): TimeSlot[] {
  const key = toDateKey(date);
  const weekday = getDay(date);
  const ranges = weekday === 0 ? [] : weekday === 6 ? SATURDAY_RANGES : WEEKDAY_RANGES;
  const slots: TimeSlot[] = [];

  for (const [start, end] of ranges) {
    for (let m = start; m + duration <= end; m += SLOT_STEP) {
      const time = `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
      const tooSoon = toDateTime(key, time).getTime() < now.getTime() + MIN_NOTICE_MS;
      const busy = pseudoRandom(key + time) < 0.45;
      slots.push({ time, available: !tooSoon && !busy && !isTaken(key, time) });
    }
  }
  return slots;
}

export function findNextAvailable(
  duration: number,
  isTaken: SlotTakenCheck,
  now = new Date()
): { date: Date; time: string } | null {
  for (const day of getBookableDays(14, now)) {
    const slot = getSlotsForDay(day, duration, isTaken, now).find((s) => s.available);
    if (slot) return { date: day, time: slot.time };
  }
  return null;
}

function pseudoRandom(seed: string): number {
  let h = 7;
  for (const char of seed) h = (h * 31 + char.charCodeAt(0)) >>> 0;
  return (h % 1000) / 1000;
}
